import { FC, ReactNode, useMemo } from 'react';
import { cn } from 'src/utils/utility';
import IconChartLine from 'src/presentations/components/atom/icons/ChartLine';
import IconChartBar from 'src/presentations/components/atom/icons/ChartBar';
import Skeleton from '@mui/material/Skeleton';
import { CardTheme, ChartType } from './types';
import { ICardAnalyticItemProps } from './types';
import { getBorderColor, getBlurColor, getIconChartColor, getHoverShadowStyles } from './cardStyles';

interface ICardAnalyticContentProps {
  item: ICardAnalyticItemProps;
  isLoading: boolean;
  readOnly?: boolean;
  isEnable?: boolean;
  isActive?: boolean;
  hasError?: boolean;
  children?: ReactNode;
}

export const CardAnalyticContent: FC<ICardAnalyticContentProps> = (props) => {
  const { item, isLoading, readOnly, isActive, isEnable, hasError, children } = props;
  const { title, cardTitle, count, icon, theme = CardTheme.NEUTRAL, chartType = ChartType.LINE, unit } = item;

  const borderColor = useMemo(() => getBorderColor({ theme, isActive, isEnable, readOnly }), [isActive, readOnly, isEnable, theme]);

  const blurColor = useMemo(() => getBlurColor({ theme }), [theme]);

  const iconChartColor = useMemo(() => getIconChartColor({ theme, isActive, isEnable }), [isActive, isEnable, theme]);

  const hoverShadowStyles = useMemo(() => (!readOnly ? getHoverShadowStyles({ theme, isActive, isEnable }) : ''), [isActive, isEnable, theme]);

  return (
    <div
      className={cn(
        'relative overflow-hidden bg-white border border-b-[3px] rounded-8 shadow-[0_2px_0px_0_rgba(112,149,221,0.12)] w-full px-10 xl:px-16 py-12 xl:py-14 flex flex-col items-start gap-y-5',
        'transition-all duration-300',
        hoverShadowStyles,
        borderColor
      )}
    >
      <div className='w-full'>
        <div className='text-left'>
          {isLoading ? (
            <div className='flex flex-col gap-4 w-full'>
              <Skeleton animation='wave' height={20} width='60%' />
              <Skeleton animation='wave' height={20} width='40%' />
            </div>
          ) : hasError ? (
            <div className='flex flex-col gap-4 w-full'>
              <h4 className='text-base xl:text-2xl font-bold text-red-500'>Failed to Load</h4>
              <p className='text-[10px] xl:text-12 text-neutral-60'>{cardTitle ?? title}</p>
            </div>
          ) : (
            <div className='flex flex-col gap-4'>
              {/* Title & Unit */}
              <div className='flex items-center'>
                <span className={cn("text-[12px] font-['Roboto']", isEnable ? 'text-[#808080]' : 'text-neutral-50')}>{cardTitle ?? title}</span>
                {unit && unit !== '#' && (
                  <>
                    <div className='w-[1px] h-[10px] bg-[#DFDFDF] mx-[6px]' />
                    <span
                      className={cn("text-[12px] font-['Roboto'] font-bold", !isEnable && 'text-neutral-50')}
                      style={{ color: isEnable ? iconChartColor : undefined }}
                    >
                      {unit}
                    </span>
                  </>
                )}
              </div>

              {/* Icon & Count */}
              <div className='flex items-center gap-[4px]'>
                <img src={icon} alt='' className={cn('w-[16px] h-[16px]', !isEnable ? 'grayscale' : '')} />
                <h4 className={cn("text-[14px] font-['Roboto'] font-bold", isEnable ? 'text-[#36454F]' : 'text-neutral-50')}>{count}</h4>
              </div>
            </div>
          )}
        </div>
      </div>

      {children}

      <div
        className={cn(
          'w-[120px] h-[120px] rounded-full blur-[70px] absolute top-[-90px] right-[-70px] opacity-0',
          (isActive || readOnly) && 'opacity-100',
          blurColor
        )}
      />
    </div>
  );
};
